<template>
  <div ref="tableUserFilterRef">
    <div
      :ref="`user-filter-${uid}`"
      class="relative flex items-center "
    >
      <button
        :id="`userSelector-${uid}`"
        :ref="`userSelector`"
        :disabled="props.isDisabled"
        class="flex items-center justify-between w-full bg-white px-3 py-2.5 leading-tight border border-[#0000001A] focus:shadow-outline select-none rounded-sm shadow-sm outline-none focus:border-main-primary focus:ring-main-primary disabled:bg-zinc-300 disabled:cursor-not-allowed disabled:pointer-events-none"
        @click.prevent="togglePanel"
      >
        <div
          class="flex flex-col items-start gap-1 font-normal text-sm"
          :class="allFilters.length ? 'text-theme-text-black': 'text-theme-subtitle-grey'"
        >
          <div
            v-if="allFilters.length"
            class="line-clamp-1"
          >
            {{ allFilters.length }} {{ cardTitle }}s Selected
          </div>
          <span
            v-else
            class="flex items-center gap-x-1"
          >
            Select {{ cardTitle }}(s)
          </span>
        </div>
        <div class="flex gap-2">
          <span class="text-gray-300">|</span>
          <ChevronDownSmall
            class="text-gray-500 duration-150 transform"
            :class="{ 'rotate-180': showTableFilter }"
          />
        </div>
      </button>
      <div
        v-if="showTableFilter"
        ref="assetsTreePanel"
        class="absolute w-full z-50 top-[105%] left-0"
      >
        <div class="px-3 pt-3 bg-white rounded-lg shadow-md">
          <span class="px-4 text-primary">
            User
          </span>
          <div class="px-4 py-2 place-content-center text-[#68737D]">
            <InputWithIcon
              :val="searchItemName"
              classes="py-2 rounded-lg"
              placeholder="Search..."
              @input="searchItemName = $event"
            >
              <template #icon>
                <SearchIcon />
              </template>
            </InputWithIcon>
          </div>
          <div class="relative max-h-[250px] overflow-y-auto">
            <div
              v-for="(item, itemIndex) in listOfItems"
              :key="itemIndex"
            >
              <label
                v-if="item.id"
                for="checkbox"
                :class="`flex gap-3 items-center py-2 px-3 w-full text-base font-normal cursor-pointer font-inter text-main-primary ${allFilters.includes(item.id) ? 'bg-gray-50' : ''}`"
              >
                <input
                  v-model="allFilters"
                  :value="item.id"
                  type="checkbox"
                  class="px-2 py-2 bg-white border-2 border-gray-300 rounded-sm cursor-pointer hover:border-gray-500 text-main-primary focus:ring-transparent focus:border-main-primary"
                >
                <span class="select-none text-md text-main-primary whitespace-nowrap no-localize-translate">
                  {{ item.name }}
                </span>
              </label>
            </div>
            <!-- template for infinite scroll -->
            <div v-if="hasNextPage">
              <div
                ref="loadMoreSearchRef"
                class="h-1 w-full"
              />
              <div
                v-if="isLoadingMoreSearch"
                class="flex justify-center py-2 bg-slate-50"
              >
                <AnimatedCircleLoader />
              </div>
            </div>
          </div>
          <hr class="h-0.5 my-4 mx-4 bg-gray-100 border-0">
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
const props = defineProps({
  customStyle: { type: String, default: 'w-full' },
  isDisabled: { type: Boolean, default: false },
  cardTitle: { type: String, default: 'User' },
  selectedItems: { type: Array, default: () => [] },
})

const emit = defineEmits(['selected'])

const { getUsersList } = useGhgDataAssignment()

const showTableFilter = ref(false)
const searchItemName = ref('')
const listOfItems = ref([])
const allFilters = ref(props.selectedItems)
const assetsTreePanel = ref(null)
const currentPage = ref(1)
const hasNextPage = ref(true)
const loadMoreSearchRef = ref(null)
const isLoadingMoreSearch = ref(false)

const tableUserFilterRef = ref(null)

onClickOutside(tableUserFilterRef, (_event) => {
  showTableFilter.value = false
})

const getUserData = async () => {
  const { data, next } = await getUsersList(currentPage.value, searchItemName.value)

  hasNextPage.value = next
  if (next) {
    currentPage.value += 1
  }
  return data
}

useIntersectionObserver(
  loadMoreSearchRef,
  async ([{ isIntersecting }]) => {
    if (isIntersecting && hasNextPage.value && !isLoadingMoreSearch.value) {
      isLoadingMoreSearch.value = true
      const result = await getUserData()
      listOfItems.value = [...listOfItems.value, ...result]
      isLoadingMoreSearch.value = false
    }
  },
)

const debouncedResetSearch = debounce(() => {
  currentPage.value = 1
  hasNextPage.value = true
  listOfItems.value = []
}, 500)

watch(searchItemName, () => {
  debouncedResetSearch()
})

watch(allFilters, (newItem) => {
  emit('selected', newItem)
})

watch(() => props.selectedItems, (newItem) => {
  allFilters.value = newItem
}, { immediate: true },
)

const uid = ref(useId())

const togglePanel = () => {
  showTableFilter.value = !showTableFilter.value
  nextTick(() => {
    if (!showTableFilter.value) {
      emitCheckedItems()
    }
  })
}

const emitCheckedItems = () => {
  emit('selected', allFilters.value)
}
</script>
